import Vue from 'vue';
import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import { GoogleMapsApi } from '@glittr/frontend-core/src/plugins/map/mapProvider/googleMapsProvider';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';
import Int64LookupModel from '@glittr/frontend-core/src/services/v2/model/int64-lookup-model';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import Tdg_CompanyDetailModel from '../../../../services/v2/model/tdg-company-detail-model';
import PostAnalyticsRequestModel from '../../../../services/v2/model/post-analytics-request-model';
import Tdg_TourItemModel from '../../../../services/v2/model/tdg-tour-item-model';
import GetMapItemsAsyncRequestModel from '../../../../services/v2/model/get-map-items-async-request-model';
import PostPDFRequestModel from '../../../../services/v2/model/post-pdf-request-model';
import Tdg_TourItemDTO from '../../../../services/v2/_generated/dto/tdg-tour-item-dto';
import GuidEventLookupModel from '../../../../services/v2/model/guid-event-lookup-model';

export default class TdgMapPage extends PageBase {
  public cookieValue:string | null = null;

  public showCompanyInfoDiv = false;

  public showCompanyList = true;

  public mapIsReady = false;

  public isLoadingPDF = false;

  public error?: string = undefined;

  public googleApi?: GoogleMapsApi;

  public map?: google.maps.Map;

  public searchBarRef = undefined as any;

  public searchBoxRef = undefined as google.maps.places.SearchBox | undefined;

  public showAddressSearchBox = false;

  public searchFieldContainerElementRef = {} as HTMLInputElement;

  public mapContainerElementRef = {} as HTMLElement;

  public myTourListItems: Tdg_CompanyDetailModel[] = [];

  public eventItems: GuidEventLookupModel[] | undefined;

  public categoryItems: Int64LookupModel[] | undefined = undefined;

  public eventIdentifier: string | undefined = undefined;

  public showMap = true;

  public professionDetails: { id: number; name: string }[] = [];

  public markers: google.maps.marker.AdvancedMarkerElement[] = [];

  public filteredCategories: number[] = [];

  public markerClusterer?: MarkerClusterer;

  public get companiesList() {
    return (
      this.companiesDataSource.data?.items.map((i) => {
        const {
          companyContactFirstName,
          companyContactLastName,
          companyStreet,
          companyHouseNumber,
          companyZIP,
          companyCity,
        } = i.data;

        const companyStreetAndHouseNumber = [companyStreet, companyHouseNumber]
          .filter(Boolean)
          .join(' ');

        return {
          ...i.data,

          companyContactName: [companyContactFirstName, companyContactLastName]
            .filter(Boolean)
            .join(' '),

          companyAddress: [
            companyStreetAndHouseNumber,
            [companyZIP, companyCity].filter(Boolean).join(' '),
          ]
            .filter(Boolean)
            .join(', '),
        };
      }) || []
    );
  }

  public set companiesList(value) { /* Do nothing */ }

  public companiesDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Companies.getCompanyProfession,
  });

  public companyDetailDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Companies.getCompanyById,
  });

  public mapItemDataSource = new DataSource({
    selectCommand: () => {
      const req = new GetMapItemsAsyncRequestModel();
      req.eventIdentifier = this.eventIdentifier;
      req.categoryId = this.mapItemDataSource.filter.categoryId;
      return Vue.$service.v2.api.tdg_Professions.getMapItemsAsync(req);
    },
  });

  public downloadPdfDataSource = new DataSource({
    insertCommand: Vue.$service.v2.api.tdg_Tour.postPDF,
  });

  public companyAnalyticEventDataSource = new DataSource({
    insertCommand: Vue.$service.v2.api.tdg_Analytics.postAnalytics,
  });

  public categoryDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Professions.lookupAllCategories,
  });

  public companyColumns = [
    { value: Vue.$t('page.companies.table.profession'), textAlign: 'left', field: 'profession' },
    { value: Vue.$t('page.companies.table.companyName'), textAlign: 'left', field: 'companyName' },
    { value: Vue.$t('page.companies.table.companyAddress'), textAlign: 'left', field: 'companyAddress' },
    { value: Vue.$t('page.companies.table.companyContactName'), textAlign: 'left', field: 'companyContactName' },
    { value: Vue.$t('page.companies.table.companyContactPhoneNumber'), textAlign: 'left', field: 'companyContactPhoneNumber' },
    { value: Vue.$t('page.companies.table.companyContactEmail'), textAlign: 'left', field: 'companyContactEmail' },
  ];

  public async initialize(): Promise<void> {
    this.eventIdentifier = this.getExternalElement();
    if (Vue.$cookie.get('TdgClientIdentifier') === null) {
      Vue.$cookie.set('TdgClientIdentifier', crypto.randomUUID());
    }
    this.cookieValue = Vue.$cookie.get('TdgClientIdentifier');
    this.updateShowCompanyList();
    this.myTourListItems = Vue.$sessionStorage.get<Tdg_CompanyDetailModel[]>('myTourElements') ?? [];
    this.googleApi = await Vue.$map.google.initialize('core', 'maps', 'marker', 'places');
    this.companiesDataSource.filter.eventIdentifier = this.eventIdentifier;
    await this.companiesDataSource.select();
    await this.loadCategoryLookup();
  }

  public async initializeTdgMap(buttons : HTMLElement[]) {
    await this.setupMap();
    for (let index = 0; index < buttons.length; index += 1) {
      const btnDiv = document.createElement('div');
      const btn = buttons[index];
      btnDiv.appendChild(btn);
      if (this.map) {
        this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(btnDiv);
      }
    }
    document.addEventListener('fullscreenchange', () => {
      const pacContainer = document.querySelector('.pac-container');
      if (!pacContainer) return;
      if (document.fullscreenElement) {
        document.fullscreenElement.appendChild(pacContainer);
      } else {
        document.body.appendChild(pacContainer);
      }
    });
    await this.mapItemDataSource.select();

    const mapItems = this.mapItemDataSource.data?.items;
    const bounds = new google.maps.LatLngBounds();

    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    mapItems?.forEach((i) => {
      const colors = i.data.profession
        ?.map((p) => p.colorHex)
        .filter((c): c is string => typeof c === 'string') ?? [];
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: new google.maps.LatLng(i.data.location!.latitude!, i.data.location!.longitude!),
        map: this.map,
        title: i.data.title,
        content: this.createCustomMarker(colors),
      });

      this.markers.push(marker);

      if (marker.position) {
        const normalizedPosition = marker.position instanceof google.maps.LatLng
          ? marker.position
          : new google.maps.LatLng(marker.position);
        bounds.extend(normalizedPosition);
      }

      marker.addListener('click', async () => {
        if (i.data.id !== undefined) {
          this.showCompanyInfoBox(i.data.id);
          if (this.cookieValue != null) {
            await this.fireEvent(i.data.id, 'viewed');
          }
        }
      });
    });

    this.markerClusterer = new MarkerClusterer({ map: this.map, markers: this.markers });

    // Wenn der Bildschirm mehr als 1000px width hat, dann ist der "myTourSideDrawer" offen und seine width wird bei der Zentrierung der Marker berücksichtigt
    // Wird weiter unten noch einmal gebraucht
    if ((mapItems?.length ?? 0) > 0) {
      const map = document.querySelector('.map') as HTMLElement;
      const myTourSideDrawer = document.querySelector('.myTourSideDrawerWrapper') as HTMLElement;
      const mapWidth = map ? map.offsetWidth : 0;
      const myTourSideDrawerWidth = myTourSideDrawer ? myTourSideDrawer.offsetWidth : 0;
      if (mapWidth > 1000) {
        this.map?.fitBounds(bounds, {
          right: myTourSideDrawerWidth,
        });
      } else {
        this.map?.fitBounds(bounds);
      }
    }
    if (this.map) {
      this.map.addListener('tilesloaded', () => {
        if (!this.mapIsReady) {
          this.mapIsReady = true;
          this.map!.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(document.getElementById('searchBarRef') as HTMLElement);
        }
      });
    }

    this.setupMapSearch(this.searchFieldContainerElementRef);
  }

  async resetAllFilters() {
    this.filteredCategories = [];
    await this.filterByCategory(undefined);
  }

  async filterByCategory(categoryId: number | undefined) {
    if (!this.showMap) {
      await this.setupMap();
      document.addEventListener('fullscreenchange', () => {
        const pacContainer = document.querySelector('.pac-container');
        if (!pacContainer) return;
        if (document.fullscreenElement) {
          document.fullscreenElement.appendChild(pacContainer);
        } else {
          document.body.appendChild(pacContainer);
        }
      });
    }
    if (categoryId === undefined) {
      this.mapItemDataSource.filter.categoryId = undefined;
      await this.mapItemDataSource.select();
    } else {
      const index = this.filteredCategories.indexOf(categoryId);
      if (index > -1) {
        this.filteredCategories.splice(index, 1);
      } else {
        this.filteredCategories.push(categoryId);
      }
      this.mapItemDataSource.filter.categoryId = this.filteredCategories;
      await this.mapItemDataSource.select();
    }

    this.markers.forEach((marker) => {
      marker.map = null;
    });
    this.markers = [];

    const mapItems = this.mapItemDataSource.data?.items;
    const bounds = new google.maps.LatLngBounds();

    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    mapItems?.forEach((i) => {
      const colors = i.data.profession
        ?.map((p) => p.colorHex)
        .filter((c): c is string => typeof c === 'string') ?? [];
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: new google.maps.LatLng(i.data.location!.latitude!, i.data.location!.longitude!),
        map: this.map,
        title: i.data.title,
        content: this.createCustomMarker(colors),
      });
      this.markers.push(marker);

      if (marker.position) {
        const normalizedPosition = marker.position instanceof google.maps.LatLng
          ? marker.position
          : new google.maps.LatLng(marker.position);
        bounds.extend(normalizedPosition);
      }

      marker.addListener('click', async () => {
        if (i.data.id !== undefined) {
          this.showCompanyInfoBox(i.data.id);
          if (this.cookieValue != null) {
            await this.fireEvent(i.data.id, 'viewed');
          }
        }
      });
    });

    this.markerClusterer = new MarkerClusterer({ map: this.map, markers: this.markers });

    // Wenn der Bildschirm mehr als 1000px width hat, dann ist der "myTourSideDrawer" offen und seine width wird bei der Zentrierung der Marker berücksichtigt
    // Wird weiter oben bereits gebraucht
    if ((mapItems?.length ?? 0) > 0) {
      const map = document.querySelector('.map') as HTMLElement;
      const myTourSideDrawer = document.querySelector('.myTourSideDrawerWrapper') as HTMLElement;
      const mapWidth = map ? map.offsetWidth : 0;
      const myTourSideDrawerWidth = myTourSideDrawer ? myTourSideDrawer.offsetWidth : 0;
      if (mapWidth > 1000) {
        this.map?.fitBounds(bounds, {
          right: myTourSideDrawerWidth,
        });
      } else {
        this.map?.fitBounds(bounds);
      }
    }
    if (this.map) {
      this.map.addListener('tilesloaded', () => {
        if (!this.mapIsReady) {
          this.mapIsReady = true;
          this.map!.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(document.getElementById('searchBarRef') as HTMLElement);
        }
      });
    }
    this.setupMapSearch(this.searchFieldContainerElementRef);

    this.companiesDataSource.filter.eventIdentifier = this.eventIdentifier;
    this.companiesDataSource.filter.categoryIds = this.mapItemDataSource.filter.categoryId;
    await this.companiesDataSource.select();
  }

  public async setupMap() {
    this.map = new google.maps.Map(
      this.mapContainerElementRef,
      {
        mapTypeId: google.maps.MapTypeId.SATELLITE,
        disableDefaultUI: false,
        zoom: 12,
        center: { lat: 47.1177271111, lng: 9.1492561111 },
        mapId: (Vue.$config.values as any)['google-mapsmapid'],
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.TOP_CENTER,
        },
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.LEFT_TOP,
        },
        scaleControl: true,
        streetViewControl: true,
        streetViewControlOptions: {
          position: google.maps.ControlPosition.LEFT_TOP,
        },
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.LEFT_TOP,
        },
      },
    );
  }

  createCustomMarker(colorsArray: string[] | string): HTMLElement {
    const inputColors = Array.isArray(colorsArray) ? colorsArray : [colorsArray];
    const uniqueColors = [...new Set(inputColors)];
    const baseColors = uniqueColors.length > 0 ? uniqueColors : [Vue.$config.values['marker-color-default']];

    const colorsAmount = baseColors.length;
    const fullPath: string[] = [];

    if (colorsAmount === 1) {
      fullPath.push(
        `<path d="M24,0 C11,0 0,11 0,24 L24,24 Z" fill="${baseColors[0]}" />`, // oben links
        `<path d="M24,0 C37,0 48,11 48,24 L24,24 Z" fill="${baseColors[0]}" />`, // oben rechts
        `<path d="M48,24 C48,41 24,72 24,72 L24,24 Z" fill="${baseColors[0]}" />`, // unten rechts
        `<path d="M0,24 C0,41 24,72 24,72 L24,24 Z" fill="${baseColors[0]}" />`, // unten links
      );
    }

    if (colorsAmount === 2) {
      fullPath.push(
        `<path d="M24,0 C11,0 0,11 0,24 L24,24 Z" fill="${baseColors[0]}" />`, // oben links
        `<path d="M24,0 C37,0 48,11 48,24 L24,24 Z" fill="${baseColors[1]}" />`, // oben rechts
        `<path d="M48,24 C48,41 24,72 24,72 L24,24 Z" fill="${baseColors[1]}" />`, // unten rechts
        `<path d="M0,24 C0,41 24,72 24,72 L24,24 Z" fill="${baseColors[0]}" />`, // unten links
      );
    }

    if (colorsAmount === 3) {
      fullPath.push(
        `<path d="M24,0 C11,0 0,11 0,24 L24,24 Z" fill="${baseColors[0]}" />`, // oben links
        `<path d="M24,0 C37,0 48,11 48,24 L24,24 Z" fill="${baseColors[0]}" />`, // oben rechts
        `<path d="M48,24 C48,41 24,72 24,72 L24,24 Z" fill="${baseColors[1]}" />`, // unten rechts
        `<path d="M0,24 C0,41 24,72 24,72 L24,24 Z" fill="${baseColors[2]}" />`, // unten links (gleiche Farbe wie unten rechts)
      );
    }

    if (colorsAmount === 4) {
      fullPath.push(
        `<path d="M24,0 C11,0 0,11 0,24 L24,24 Z" fill="${baseColors[0]}" />`, // oben links
        `<path d="M24,0 C37,0 48,11 48,24 L24,24 Z" fill="${baseColors[1]}" />`, // oben rechts
        `<path d="M48,24 C48,41 24,72 24,72 L24,24 Z" fill="${baseColors[2]}" />`, // unten rechts
        `<path d="M0,24 C0,41 24,72 24,72 L24,24 Z" fill="${baseColors[3]}" />`, // unten links
      );
    }

    const segmentsToShow = (colorsAmount === 1 || colorsAmount === 2 || colorsAmount === 3) ? 4 : colorsAmount;

    const svg = `
      <svg width="30" height="40" viewBox="0 0 48 72" xmlns="http://www.w3.org/2000/svg">
        ${fullPath.slice(0, segmentsToShow).join('\n')}
        <circle cx="24" cy="24" r="10" fill="#FFFFFF" />
      </svg>
    `;

    const div = document.createElement('div');
    div.style.width = '30px';
    div.style.height = '40px';
    div.innerHTML = svg;
    return div;
  }

  public async addItemToMyTour(id: number | undefined, element: Tdg_CompanyDetailModel) {
    const existingElement = this.myTourListItems.find((item) => item.id === id);
    if (existingElement) {
      this.error = 'Diese Firma ist bereits in der Liste.';
      return;
    }

    this.myTourListItems.push(element);
    Vue.$sessionStorage.set('myTourElements', this.myTourListItems);
    this.hideCompanyInfoBox();
    this.showCompanyList = true;
    if (this.cookieValue != null) {
      await this.fireEvent(element.id, 'tourAdded');
    }
  }

  public async fireEvent(companyId:number | undefined, eventName:string) {
    if (!companyId) return;
    await this.companyAnalyticEventDataSource.insert({
      companyId,
      eventIdentifier: this.eventIdentifier,
      analyticEventTypeId: eventName,
      clientIdentifier: this.cookieValue ?? '',
    } as PostAnalyticsRequestModel);
  }

  public async removeItemFromMyTour(id: number) {
    this.myTourListItems = this.myTourListItems.filter((item) => item.id !== id);
    Vue.$sessionStorage.set('myTourElements', this.myTourListItems);
    this.showCompanyList = true;
    if (this.cookieValue != null) {
      await this.fireEvent(id, 'tourRemoved');
    }
  }

  public async downloadPdf() {
    if (this.isLoadingPDF) return;
    this.isLoadingPDF = true;
    try {
      const storedTourData = Vue.$sessionStorage.get<Tdg_CompanyDetailModel[]>('myTourElements') ?? [];
      const requestData = new PostPDFRequestModel({
        items: storedTourData.map((company) => {
          const item = new Tdg_TourItemModel({
            companyId: company.id!,
            orderId: company.id!,
          });
          return item as unknown as Tdg_TourItemDTO;
        }),
      });

      requestData.eventIdentifier = this.eventIdentifier;
      const response = await this.downloadPdfDataSource.insert(requestData);

      if (response && response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'Meine_Tour.pdf';
        link.click();
        if (this.cookieValue != null) {
          storedTourData.forEach(async (company) => {
            await this.fireEvent(company.id, 'tourDownloaded');
          });
        }
      }
    } finally {
      this.isLoadingPDF = false;
    }
  }

  public searchForAddress() {
    this.setupMapSearch(this.searchFieldContainerElementRef);
    this.searchFieldContainerElementRef.value = '';
    this.showAddressSearchBox = true;
    this.googleApi!.core.event.addListenerOnce(this.map!, 'idle', () => {
      this.mapContainerElementRef.firstChild?.appendChild(this.searchBarRef as HTMLElement);
    });
  }

  public setupMapSearch(searchBoxHTMLef:HTMLInputElement) {
    this.searchBoxRef = new google.maps.places.SearchBox(searchBoxHTMLef);
    this.googleApi!.core.event.addListener(this.searchBoxRef, 'places_changed', () => {
      const searchResult = this.searchBoxRef!.getPlaces();
      if (searchResult === undefined || searchResult.length === 0) {
        return;
      }
      const searchResultLat = searchResult[0].geometry?.location?.lat();
      const searchResultLng = searchResult[0].geometry?.location?.lng();
      if (searchResultLat && searchResultLng) {
        const panPoint = new google.maps.LatLng(searchResultLat, searchResultLng);
        this.map?.panTo(panPoint);
      }
      this.showAddressSearchBox = false;
    });
  }

  public findUserPosition() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const userLatLng = new google.maps.LatLng(
            position.coords.latitude,
            position.coords.longitude,
          );
          this.map?.panTo(userLatLng);
        },
        () => {},
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    }
  }

  public async showCompanyInfoBox(companyId: number) {
    try {
      this.companyDetailDataSource.filter.id = companyId;
      this.companyDetailDataSource.filter.eventIdentifier = this.eventIdentifier;
      await this.companyDetailDataSource.select();

      this.showCompanyInfoDiv = true;
    } finally {
      this.scrollToTopOfInfoBox();
    }
  }

  public hideCompanyInfoBox() {
    this.showCompanyInfoDiv = false;
    this.error = undefined;
  }

  public scrollToTopOfInfoBox() {
    setTimeout(() => {
      const infoBoxWrapper = document.querySelector('.infoBoxWrapper') as HTMLElement;
      if (infoBoxWrapper) {
        infoBoxWrapper.scrollTo({
          top: 0,
        });
      }
    });
  }

  private updateShowCompanyList() {
    if (window.matchMedia('(max-width: 1000px)').matches) {
      this.showCompanyList = false;
    }
  }

  public getExternalElement() {
    const configKey = 'current-eventidentifier';
    const externalElementId = Vue.$config.getQueryOrValue(configKey);
    return externalElementId;
  }

  async loadCategoryLookup() {
    try {
      await this.categoryDataSource.select();
      if (this.categoryDataSource.data) {
        this.categoryItems = this.categoryDataSource.data.items.map((i) => i.data);
      }
    } catch (error) {
      Vue.$log.error(`Failed to load organisations: ${error}`);
    }
  }

  switchMode() {
    if (this.showMap === true) {
      this.showMap = false;
    } else if (this.showMap === false) {
      this.showMap = true;
    }
  }

  updateSideDrawerHeight() {
    const mapWrapper = document.querySelector('.tdgMapWrapper') as HTMLElement;
    const listWrapper = document.querySelector('.tdgListWrapper') as HTMLElement;
    const sideDrawer = document.querySelector('.myTourSideDrawerWrapper') as HTMLElement;

    if (this.showMap === true) {
      sideDrawer.style.height = `${mapWrapper.offsetHeight}px`;
    }
    if (this.showMap === false) {
      sideDrawer.style.height = `${listWrapper.offsetHeight}px`;
    }
  }
}
